import type SignalCreateDTO from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalCreateDTO";
import { SignalTimeframePeriod } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalTimeframe";
import type SignalUpdateDTO from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalUpdateDTO";
import type UISignal from "app/pages/sales-intelligence/sub-modules/signals/models/view/UISignal";

import type Signal from "../core/Signal";
import type SignalFilters from "../core/SignalFilters";
import SignalSource from "../core/SignalSource";
import SignalType from "../core/SignalType";
import type { SignalTemplateDeps } from "../templates/SignalTemplate";
import SignalTemplate from "../templates/SignalTemplate";

import type TechnologySignalFilters from "./TechnologySignalFilters";

type TechnologySignalTemplateDeps = Omit<SignalTemplateDeps<TechnologySignalFilters>, "signalType">;

class TechnologySignalTemplate extends SignalTemplate<TechnologySignalFilters> {
    static defaultValues = {
        technologies: null,
        technologyCategories: null,
        technologySubCategories: null,
        signalSource: SignalSource.Platform,
        timeFrame: {
            period: SignalTimeframePeriod.Month,
            periodAmount: 1,
        },
    };

    constructor(deps: TechnologySignalTemplateDeps) {
        super({ ...deps, signalType: SignalType.Technology });
    }

    isValid(): boolean {
        return typeof this.signalSubType !== "undefined";
    }

    matchesSignal(signal: Readonly<Signal<TechnologySignalFilters>>): boolean {
        return this.signalSubType === signal.signalSubType && this.filters.type === signal.filters.type;
    }

    getSignalCreateDTO<F extends SignalFilters>(
        filters: Readonly<F>,
        signalSource: SignalSource,
    ): Readonly<SignalCreateDTO<F>> {
        return {
            isDefault: false,
            signalSource: signalSource,
            signalType: this.signalType,
            signalSubType: this.signalSubType,
            lifecycleStatus: this.lifecycleStatus,
            filters,
        };
    }

    getSignalUpdateDTO<F extends SignalFilters>(
        uiSignal: Readonly<UISignal<F>>,
        filters: Readonly<F>,
    ): Readonly<SignalUpdateDTO<F>> {
        const { id, isDefault } = uiSignal;
        return {
            id,
            isDefault,
            signalType: this.signalType,
            signalSubType: this.signalSubType,
            filters,
        };
    }
}

export default TechnologySignalTemplate;
